import { motion } from "framer-motion";
import { IMG, waLink } from "@/lib/brand";
import { Home, FileText, Handshake, Languages, ArrowUpRight } from "lucide-react";
import { useT } from "@/lib/i18n";

export default function Services() {
  const { t } = useT();
  const services = [
    { id: "search", titleKey: "srv.search.title", descKey: "srv.search.desc", pointsKey: "srv.search.points", image: IMG.interior, Icon: Home },
    { id: "documents", titleKey: "srv.docs.title", descKey: "srv.docs.desc", pointsKey: "srv.docs.points", image: IMG.documents, Icon: FileText },
    { id: "viewings", titleKey: "srv.view.title", descKey: "srv.view.desc", pointsKey: "srv.view.points", image: IMG.handshake, Icon: Handshake },
    { id: "languages", titleKey: "srv.lang.title", descKey: "srv.lang.desc", pointsKey: null, image: null, Icon: Languages },
  ];

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-[#0B1733]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="text-xs tracking-[0.3em] text-gold uppercase mb-4">{t("srv.eyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white max-w-2xl leading-tight">
              {t("srv.title.1")}
              <br />
              <span className="italic text-gold">{t("srv.title.2")}</span>
            </h2>
          </div>
          <p className="text-slate-400 max-w-md leading-relaxed">{t("srv.blurb")}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((s, idx) => {
            const title = t(s.titleKey);
            const points = s.pointsKey ? t(s.pointsKey) : [];
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: idx * 0.08 }}
                data-testid={`service-card-${s.id}`}
                className="group relative overflow-hidden rounded-3xl border border-gold/20 bg-navy-800 hover:border-gold/60 transition-[border-color] duration-500 min-h-[420px] flex"
              >
                {s.image && (
                  <div className="absolute inset-0 opacity-30 group-hover:opacity-40 transition-opacity duration-700">
                    <img src={s.image} alt="" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/80 to-navy-900/40" />
                  </div>
                )}
                <div className="relative p-7 md:p-8 h-full w-full flex flex-col justify-between">
                  <div>
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-gold/10 border border-gold/30 text-gold mb-6">
                      <s.Icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-serif text-2xl text-white leading-tight mb-3">{title}</h3>
                    <p className="text-sm text-slate-300/90 leading-relaxed">{t(s.descKey)}</p>
                  </div>
                  <div className="mt-6 flex items-end justify-between gap-3">
                    <ul className="flex flex-wrap gap-1.5">
                      {(Array.isArray(points) ? points : []).slice(0, 3).map((p) => (
                        <li key={p} className="text-[11px] text-gold-light bg-navy-700/70 border border-gold/20 rounded-full px-2.5 py-1">
                          {p}
                        </li>
                      ))}
                    </ul>
                    <a
                      href={waLink(t("srv.cta_about", { title }))}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-cta-${s.id}`}
                      className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-navy-900 transition-[color,background-color,border-color,box-shadow,transform]"
                      aria-label={title}
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
